import { Spinner } from '@/components/glitch-ui/spinner';
import { useNavigate, Link } from '@tanstack/react-router';
import { Button } from '@/components/ui/button';
import { Megaphone, ArrowRight } from 'lucide-react';
import { useAtomValue } from 'jotai';
import { organizationDataAtom } from '@/store';
import { useQuery } from '@tanstack/react-query';
import { businessQueryOptions, businessTaskTrackingQueryOptions } from '@/lib/query-options';
import { GeneratingDots } from '@/components/glitch-ui/generating-dots';

export function CampaignEmptyState() {
  const navigate = useNavigate({ from: '/campaigns' });
  const organizationData = useAtomValue(organizationDataAtom);

  const { data: business, isLoading } = useQuery({
    ...businessQueryOptions(organizationData?.id || 0),
    enabled: !!organizationData?.id,
  });

  const { data: taskTracking } = useQuery({
    ...businessTaskTrackingQueryOptions(business?.id),
    enabled: !!business?.id,
  });
  const isGeneratingBrandIntel = taskTracking?.is_generating ?? false;

  // Show loading state
  if (isLoading) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[50vh] text-center">
        <Spinner size="xl" className="text-gray-400" />
      </div>
    );
  }

  // No business exists - inform user to create business first (passive state, no action)
  if (!business) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[50vh] text-center">
        <Megaphone className="h-12 w-12 text-gray-500 mb-4" strokeWidth={1} />
        <h3 className="text-lg font-semibold text-gray-500 mb-2">No Campaigns Yet</h3>
        <p className="text-gray-500">
          Define your <Link to="/business" className="text-[#290BD9] hover:text-[#2309C5] underline">business</Link> to start creating campaigns
        </p>
      </div>
    );
  }

  // Business exists but brand intel is still generating - block campaign creation
  if (isGeneratingBrandIntel) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[50vh] text-center">
        <Megaphone className="h-12 w-12 text-gray-500 mb-4" strokeWidth={1} />
        <h3 className="text-lg font-semibold text-gray-500 mb-2">Analyzing Your Business</h3>
        <p className="text-gray-500 mb-4">Campaign creation will be available once your business profile is ready</p>
        <GeneratingDots count={5} />
      </div>
    );
  }

  // Brand exists and ready - show campaign creation option (active state with action)
  return (
    <div className="flex flex-col items-center justify-center min-h-[50vh] text-center">
      <Megaphone className="h-12 w-12 text-gray-500 mb-4" strokeWidth={1} />
      <h3 className="text-lg font-semibold text-gray-500 mb-2">No Campaigns</h3>
      <p className="text-gray-500 mb-6">Create your first campaign to start advertising</p>
      <Button
        onClick={() => navigate({ to: '/new/campaign' })}
        size="lg"
        className="w-[280px] py-6 bg-[#290BD9] hover:bg-[#2309C5] text-white shadow-lg flex items-center justify-center space-x-2"
      >
        <span>CREATE CAMPAIGN</span>
        <ArrowRight className="h-4 w-4" />
      </Button>
    </div>
  );
}
